import { selectApiData, selectApiNoContent } from 'util/selector/apiSelector';

import { ApiMessage } from 'api/const';
import { WithData } from 'api/interfaces';
import { AxiosInstance, AxiosResponse } from 'axios';

import {
  DemographicDto,
  DemographicModel,
  mapDemographicDtoToModel,
  mapDemographicModelToDto,
} from './dto/Demographic.dto';

export namespace DemographicApi {}

export class DemographicApi {
  constructor(
    private client: AxiosInstance,
    private reportClient: AxiosInstance,
  ) {}

  list(projectId: number): Promise<DemographicModel[]> {
    return this.client
      .get(`/api/v1/project/${projectId}/demographic`)
      .then(selectApiData)
      .then((entries) => entries.map(mapDemographicDtoToModel));
  }

  create(projectId: number, data: DemographicModel): Promise<DemographicModel> {
    return this.client
      .post<
        WithData<DemographicDto>
      >(`/api/v1/project/${projectId}/demographic`, mapDemographicModelToDto(data))
      .then(selectApiData)
      .then(mapDemographicDtoToModel);
  }

  createDefault(projectId: number, data: DemographicModel): Promise<DemographicModel> {
    return this.client
      .post<
        WithData<DemographicDto>
      >(`/api/v1/project/${projectId}/demographic-constant`, mapDemographicModelToDto(data))
      .then(selectApiData)
      .then(mapDemographicDtoToModel);
  }

  update(
    projectId: number,
    demographicId: number,
    demographic: DemographicModel,
  ): Promise<DemographicModel> {
    return this.client
      .post<
        WithData<DemographicDto>
      >(`/api/v1/demographic/${demographicId}`, mapDemographicModelToDto(demographic))
      .then(selectApiData)
      .then(mapDemographicDtoToModel);
  }

  upload(projectId: number, file: File) {
    const data = new FormData();
    data.append('file', file);

    return this.client.post(`/api/v1/project/${projectId}/demography-submit`, data);
  }

  preview(projectId: number, file: File) {
    const data = new FormData();
    data.append('file', file);

    return this.client
      .post<WithData<DemographicDto[]>>(`/api/v1/project/${projectId}/demography-preview`, data)
      .then(selectApiData)
      .then((entries) => entries.map(mapDemographicDtoToModel));
  }

  downloadChangeReport(projectId: number, file: File): Promise<AxiosResponse<Blob>> {
    const data = new FormData();
    data.append('file', file);

    return this.client.post<Blob>(`/api/v1/project/${projectId}/demography-preview`, data, {
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      responseType: 'blob',
    });
  }

  delete(projectId: number, demographicId: number): Promise<void> {
    return this.client
      .delete<ApiMessage.Deleted>(`/api/v1/demographic/${demographicId}`)
      .then(selectApiNoContent);
  }

  reorder(projectId: number, list: number[]): Promise<void> {
    return this.client
      .post<void>(`/api/v1/demographic-reorder`, { order: list })
      .then(selectApiNoContent);
  }

  listReport(projectId: number): Promise<DemographicModel[]> {
    return this.reportClient
      .get(`/api/v1/project/${projectId}/demography`)
      .then((response) => response.data.data)
      .then((entries) => entries.map(mapDemographicDtoToModel));
  }
}
